import { ItemsPerPage } from '@billy/design-system'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useMemo } from 'react'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { usePrevious } from 'react-use'

import { Contact } from '@features/contacts/types/contact'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { SortDirection } from '../../../enums/sortDirection'
import { ContactsSortProperty, fetchContactsAndContactPersons, FetchContactsResponseData } from '../query-api'

interface UseFetchContactsInfiniteProps {
  isArchived?: boolean
  isCustomer?: boolean
  isSupplier?: boolean
  pageSize?: ItemsPerPage
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: ContactsSortProperty
}

export const useFetchContactsInfinite = ({
  isArchived,
  isCustomer,
  isSupplier,
  pageSize,
  searchQuery,
  sortDirection,
  sortProperty,
}: UseFetchContactsInfiniteProps) => {
  const { organization } = useUserOrganization()
  const queryClient = useQueryClient()

  const queryProps = useMemo(
    () => ({
      ...(pageSize ? { pageSize: pageSize as ItemsPerPage } : {}),
      ...(searchQuery ? { q: searchQuery } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      ...(sortProperty ? { sortProperty } : {}),
      organizationId: organization?.id as string,
      isArchived,
      isCustomer,
      isSupplier,
    }),
    [isArchived, isCustomer, isSupplier, organization?.id, pageSize, searchQuery, sortDirection, sortProperty],
  )

  // 'infinite' marker keeps this cache slot separate from the useQuery-based useFetchContacts
  const queryKey = useMemo(() => [QueryKeys.ContactsAndContactPersons, 'infinite', queryProps] as const, [queryProps])

  // Cancel the previous in-flight request when query params (typically searchQuery) change,
  // so we don't pile up parallel network calls while the user keeps typing.
  const previousQueryKey = usePrevious(queryKey)
  useEffect(() => {
    if (previousQueryKey && !isEqual(previousQueryKey, queryKey)) {
      queryClient.cancelQueries(previousQueryKey)
    }
  }, [previousQueryKey, queryKey, queryClient])

  const handleGetNextPage = useCallback((lastPage: FetchContactsResponseData) => {
    const paging = lastPage.meta?.paging
    if (!paging || paging.page >= paging.pageCount) {
      return undefined
    }
    return paging.page + 1
  }, [])

  const { data, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => fetchContactsAndContactPersons({ ...queryProps, page: pageParam }),
    {
      enabled: !!organization?.id,
      staleTime: defaultStaleTime,
      getNextPageParam: handleGetNextPage,
    },
  )

  const contacts = useMemo(() => {
    // Dedupe contacts that may appear on more than one page (e.g. when paging shifts
    // between requests). The latest occurrence wins, so we always reflect the freshest
    // server response for a given contact.
    const contactsById = new Map<string, Contact>()
    for (const page of data?.pages ?? []) {
      for (const contact of page.contacts ?? []) {
        if (contact?.id) {
          contactsById.set(contact.id, contact)
        }
      }
    }
    return [...contactsById.values()]
  }, [data?.pages])

  return {
    contacts,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  }
}
